import { useCallback, useEffect, useState } from "react";
import { accessTokenApi } from "@/api/access-token";
import { useConfirm } from "@/components/feedback/Confirm";
import { useToast } from "@/components/feedback/Toast";
import { Button } from "@/components/primitives/Button";
import { FloatingCard } from "@/components/primitives/FloatingCard";
import { useSession } from "@/session/SessionProvider";

export default function Account() {
	const { user } = useSession();
	const { toast } = useToast();
	const confirm = useConfirm();
	const [token, setToken] = useState<string | null | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<"generate" | "regenerate" | "revoke" | null>(
		null,
	);
	const [copied, setCopied] = useState(false);

	const load = useCallback(async () => {
		try {
			const data = await accessTokenApi.get();
			setToken(data.token);
			setError(null);
		} catch (e) {
			setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
			setToken(null);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const accessUrl = token
		? `${window.location.origin}/access?token=${token}`
		: "";

	const onGenerate = async (mode: "generate" | "regenerate") => {
		if (mode === "regenerate") {
			const ok = await confirm({
				title: "Link neu erzeugen?",
				description:
					"Der alte Lesezeichen-Link funktioniert danach nicht mehr.",
				confirmLabel: "Neu erzeugen",
				variant: "danger",
			});
			if (!ok) return;
		}
		setBusy(mode);
		setError(null);
		try {
			const data = await accessTokenApi.create();
			setToken(data.token);
			toast(
				mode === "regenerate" ? "Zugangslink erneuert" : "Zugangslink erzeugt",
				"success",
			);
		} catch (e) {
			setError(e instanceof Error ? e.message : "Erzeugen fehlgeschlagen");
		} finally {
			setBusy(null);
		}
	};

	const onRevoke = async () => {
		const ok = await confirm({
			title: "Zugangslink widerrufen?",
			description: "Lesezeichen melden dich danach nicht mehr an.",
			confirmLabel: "Widerrufen",
			variant: "danger",
		});
		if (!ok) return;
		setBusy("revoke");
		setError(null);
		try {
			await accessTokenApi.revoke();
			setToken(null);
			toast("Zugangslink widerrufen", "success");
		} catch (e) {
			setError(e instanceof Error ? e.message : "Widerruf fehlgeschlagen");
		} finally {
			setBusy(null);
		}
	};

	const onCopy = async () => {
		if (!accessUrl) return;
		try {
			await navigator.clipboard.writeText(accessUrl);
			setCopied(true);
			toast("Link kopiert", "success");
			setTimeout(() => setCopied(false), 1500);
		} catch {
			toast("Kopieren fehlgeschlagen", "error");
		}
	};

	return (
		<div className="flex flex-1 justify-center px-4 py-8">
			<FloatingCard className="w-full max-w-lg p-5">
				<h1 className="text-lg font-semibold tracking-tight">Konto</h1>
				<p className="mt-0.5 text-sm text-fg-muted">
					{user?.email}
					{user?.isAdmin ? " · Admin" : ""}
				</p>

				<section className="mt-5 border-t border-zinc-950/[0.06] pt-5 dark:border-white/[0.06]">
					<h2 className="text-sm font-semibold tracking-tight">
						Direkter Zugangslink
					</h2>
					<p className="mt-1 text-sm text-fg-muted">
						Persönliche URL, die dich ohne Passwort anmeldet. Nur auf vertrauenswürdigen
						Geräten als Lesezeichen speichern — wer den Link hat, hat vollen
						Kontozugriff.
					</p>

				{error ? <p className="mt-4 text-sm text-rose-500">{error}</p> : null}

				{token === undefined && !error ? (
					<p className="mt-4 text-sm text-fg-muted">Laden…</p>
				) : null}

				{token === null ? (
					<div className="mt-4 space-y-2">
						<Button
							onClick={() => void onGenerate("generate")}
							disabled={busy !== null}
						>
							{busy === "generate" ? "…" : "Zugangslink erzeugen"}
						</Button>
						<p className="text-xs text-fg-muted">
							Du kannst den Link jederzeit widerrufen oder ersetzen.
						</p>
					</div>
				) : null}

				{token ? (
					<div className="mt-4 space-y-4">
						<div className="flex items-center gap-2 rounded-[var(--radius-control)] bg-surface-raised p-2 pl-3">
							<span className="min-w-0 flex-1 truncate font-mono text-xs text-teal-600 dark:text-teal-400">
								{accessUrl}
							</span>
							<Button
								size="sm"
								variant="secondary"
								onClick={() => void onCopy()}
								disabled={busy !== null}
							>
								{copied ? "Kopiert" : "Kopieren"}
							</Button>
						</div>
						<div className="flex flex-wrap gap-2">
							<Button
								variant="secondary"
								size="sm"
								onClick={() => void onGenerate("regenerate")}
								disabled={busy !== null}
							>
								{busy === "regenerate" ? "…" : "Neu erzeugen"}
							</Button>
							<Button
								variant="danger"
								size="sm"
								onClick={() => void onRevoke()}
								disabled={busy !== null}
							>
								{busy === "revoke" ? "…" : "Widerrufen"}
							</Button>
						</div>
						<p className="text-xs text-fg-muted">
							Nur der Lesezeichen-Link wird ungültig. Offene Sessions bleiben bis zum
							Abmelden gültig.
						</p>
					</div>
				) : null}
				</section>
			</FloatingCard>
		</div>
	);
}
